import { collection, addDoc, deleteDoc, doc, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { SavedJourney } from '../types';

function colRef(uid: string) {
  return collection(db, 'users', uid, 'savedJourneys');
}

function stripUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    const cleaned = value
      .map((v) => (v === undefined ? undefined : stripUndefinedDeep(v)))
      // Remove any undefined items that would produce sparse arrays Firestore can't handle
      .filter((v) => v !== undefined);
    return cleaned as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue; // Firestore does not allow undefined
      out[k] = stripUndefinedDeep(v as unknown);
    }
    return out as unknown as T;
  }
  return value;
}

export async function listSavedJourneys(uid: string): Promise<SavedJourney[]> {
  const q = query(colRef(uid), orderBy('savedAt', 'desc'));
  try {
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<SavedJourney, 'id'>) }));
  } catch (err: any) {
    console.error('[RouteX][Firestore] listSavedJourneys:error', err?.code ?? err);
    throw err;
  }
}

export async function createSavedJourney(uid: string, journey: Omit<SavedJourney, 'id'>): Promise<SavedJourney> {
  try {
    const clean = stripUndefinedDeep(journey);
    const ref = await addDoc(colRef(uid), clean);
    return { id: ref.id, ...journey };
  } catch (err: any) {
    console.error('[RouteX][Firestore] createSavedJourney:error', err?.code ?? err);
    throw err;
  }
}

export async function deleteSavedJourney(uid: string, journeyId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'users', uid, 'savedJourneys', journeyId));
  } catch (err: any) {
    console.error('[RouteX][Firestore] deleteSavedJourney:error', err?.code ?? err);
    throw err;
  }
}
